import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import toastr from 'toastr';
import { questionnairesAPI } from './utils/api';
import RecruiterHeader from './components/recruiter/RecruiterHeader';
import './styles/design-system.css';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DrawerProvider } from './components/shared/DrawerManager';
import Login from './components/Login';
import InterviewerDashboard from './components/interviewer/InterviewerDashboard';
import CandidateDashboard from './components/CandidateDashboard';
import AdminDashboard from './components/admin/AdminDashboard';
import People from './components/admin/People';
import Assessments from './components/admin/Assessments';
import JobsManagement from './components/admin/JobsManagement';
import SystemAnalytics from './components/admin/SystemAnalytics';
import PipelineHealth from './components/admin/PipelineHealth';
import TrainingAdmin from './components/admin/TrainingAdmin';
import PermissionManagement from './components/admin/PermissionManagement';
import CompanySettings from './components/admin/CompanySettings';
import TeamsIntegration from './components/admin/TeamsIntegration';
import RegisterDashboard from './components/candidate/RegisterDashboard';
import AssessmentResults from './components/AssessmentResults';
import AssessmentLab from './components/AssessmentLab';
import RecruiterDashboard from './components/recruiter/RecruiterDashboard';
import RecruiterJobsManagement from './components/recruiter/JobsManagement';
import ApplicationsList from './components/recruiter/ApplicationsList';
import ApprovalDashboard from './components/recruiter/ApprovalDashboard';
import AssessmentDetails from './components/recruiter/AssessmentDetails';
import AssessmentCreateWizard from './components/recruiter/AssessmentCreateWizard';
import QuestionnaireBuilder from './components/recruiter/QuestionnaireBuilder';
import TalentPoolView from './components/recruiter/TalentPoolView';
import TrainingDashboard from './components/training/TrainingDashboard';
import ChallengeDetail from './components/ChallengeDetail';
import JobPosting from './components/JobPosting';
import JobApplicationForm from './components/JobApplicationForm';
import PublicJobListings from './components/PublicJobListings';
import PublicHome from './components/PublicHome';
import EmailVerification from './components/EmailVerification';
import MagicLinkVerify from './components/MagicLinkVerify';
import LabSharePage from './components/LabSharePage';
import ApplicationTracker from './components/ApplicationTracker';
import InterviewFeedback from './components/InterviewFeedback';
import QuestionnaireResponse from './pages/QuestionnaireResponse';
import AvailabilityPicker from './pages/AvailabilityPicker';
import MyLabPage from './pages/MyLabPage';

// Admin Panel wrapper using URL-based navigation
const AdminPanelWrapper = ({ children, currentView }) => {
  const navigate = useNavigate();

  const handleNavigate = (view) => {
    navigate(`/admin/${view}`);
  };

  return React.cloneElement(children, { onNavigate: handleNavigate, currentView });
};

// Wrapper component for QuestionnaireBuilder to extract route params and load data
const QuestionnaireBuilderWrapper = () => {
  const { jobId, questionnaireId } = useParams();
  const navigate = useNavigate();
  const [existingQuestionnaire, setExistingQuestionnaire] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (questionnaireId) {
      loadQuestionnaire();
    }
  }, [questionnaireId]);

  const loadQuestionnaire = async () => {
    try {
      setLoading(true);
      const result = await questionnairesAPI.getById(questionnaireId);
      setExistingQuestionnaire(result.data.questionnaire);
    } catch (error) {
      console.error('Failed to load questionnaire:', error);
      toastr.error('Failed to load questionnaire');
      navigate(`/recruiter/jobs/${jobId}/applications`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div>
        <RecruiterHeader currentView="jobs" />
        <div style={{ padding: '2rem', textAlign: 'center' }}>Loading questionnaire...</div>
      </div>
    );
  }

  return (
    <div>
      <RecruiterHeader currentView="jobs" />
      <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
            {questionnaireId ? 'Edit Questionnaire' : 'Create Questionnaire'}
          </h1>
          <button
            onClick={() => navigate(`/recruiter/jobs/${jobId}/applications`)}
            style={{
              padding: '0.5rem 1rem',
              background: '#F3F4F6',
              color: '#374151',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            Cancel
          </button>
        </div>
        <QuestionnaireBuilder
          jobPostingId={jobId}
          existingQuestionnaire={existingQuestionnaire}
          onClose={() => navigate(`/recruiter/jobs/${jobId}/applications`)}
          onSave={() => {
            toastr.success(questionnaireId ? 'Questionnaire updated successfully!' : 'Questionnaire created successfully!');
            navigate(`/recruiter/jobs/${jobId}/applications`);
          }}
        />
      </div>
    </div>
  );
};

// Protected Route component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  const authenticated = isAuthenticated();
  console.log('[ProtectedRoute]', location.pathname, '- authenticated:', authenticated, 'user:', user?.email, 'role:', user?.role, 'allowedRoles:', allowedRoles);

  if (!authenticated) {
    // Store the full attempted URL (pathname + search + hash) for redirect after login
    const fullPath = location.pathname + location.search + location.hash;
    // Don't store redirect if it contains SSO params (prevents redirect loop)
    if (!fullPath.includes('sessionId=')) {
      sessionStorage.setItem('redirectAfterLogin', fullPath);
      console.log('[ProtectedRoute] NOT authenticated - storing redirect URL:', fullPath, '- redirecting to /login');
    }
    return <Navigate to="/login" replace />;
  }

  // Admin is a super-user for UI access across all role-based routes.
  if (allowedRoles.length > 0 && user?.role !== 'admin' && !allowedRoles.includes(user?.role)) {
    console.log('[ProtectedRoute] Role mismatch - user role:', user?.role, 'allowed:', allowedRoles);
    return <Navigate to="/" replace />;
  }

  return children;
};

// Main App component
const AppContent = () => {
  const { user, loading, isAuthenticated } = useAuth();
  const hasRoleAccess = (roles = []) => (
    isAuthenticated() &&
    (user?.role === 'admin' || roles.includes(user?.role))
  );

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <div className="cyber-spinner"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<RegisterDashboard />} />
      <Route path="/lab/share/:token" element={<LabSharePage />} />

      {/* Admin routes with URL-based navigation */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Navigate to="/admin/dashboard" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPanelWrapper currentView="dashboard">
              <AdminDashboard />
            </AdminPanelWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/people"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPanelWrapper currentView="people">
              <People />
            </AdminPanelWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/assessments"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPanelWrapper currentView="assessments">
              <Assessments />
            </AdminPanelWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/jobs"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPanelWrapper currentView="jobs">
              <JobsManagement />
            </AdminPanelWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/training"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPanelWrapper currentView="training">
              <TrainingAdmin />
            </AdminPanelWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPanelWrapper currentView="analytics">
              <SystemAnalytics />
            </AdminPanelWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pipeline"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPanelWrapper currentView="pipeline">
              <PipelineHealth />
            </AdminPanelWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/permissions"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPanelWrapper currentView="permissions">
              <PermissionManagement />
            </AdminPanelWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPanelWrapper currentView="settings">
              <CompanySettings />
            </AdminPanelWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/integrations"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminPanelWrapper currentView="integrations">
              <TeamsIntegration />
            </AdminPanelWrapper>
          </ProtectedRoute>
        }
      />

      {/* Role-based default routes */}
      <Route
        path="/"
        element={
          isAuthenticated() ? (
            user?.role === 'admin' ? <Navigate to="/admin/dashboard" replace /> :
            user?.role === 'interviewer' ? <Navigate to="/interviewer/stats" replace /> :
            user?.role === 'candidate' ? <CandidateDashboard user={user} /> :
            user?.role === 'recruiter' ? <Navigate to="/recruiter/assessments" replace /> :
            user?.role === 'stakeholder' ? <div>Stakeholder Dashboard (Coming Soon)</div> :
            <Navigate to="/login" replace />
          ) : (
            <PublicHome />
          )
        }
      />

      {/* Interviewer Routes */}
      <Route
        path="/interviewer/stats"
        element={
          <ProtectedRoute allowedRoles={['interviewer', 'recruiter']}>
            <InterviewerDashboard user={user} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/interviewer/schedule"
        element={
          <ProtectedRoute allowedRoles={['interviewer', 'recruiter']}>
            <InterviewerDashboard user={user} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/interviewer/queue"
        element={
          <ProtectedRoute allowedRoles={['interviewer', 'recruiter']}>
            <InterviewerDashboard user={user} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/interviewer/assessments"
        element={
          <ProtectedRoute allowedRoles={['interviewer', 'recruiter']}>
            <InterviewerDashboard user={user} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/interviewer/live"
        element={
          <ProtectedRoute allowedRoles={['interviewer', 'recruiter']}>
            <InterviewerDashboard user={user} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/interviewer/evaluate"
        element={
          <ProtectedRoute allowedRoles={['interviewer', 'recruiter']}>
            <InterviewerDashboard user={user} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/interviewer/lab"
        element={
          <ProtectedRoute allowedRoles={['interviewer', 'recruiter']}>
            <InterviewerDashboard user={user} />
          </ProtectedRoute>
        }
      />

      {/* Recruiter Routes */}
      <Route
        path="/recruiter/assessments"
        element={
          hasRoleAccess(['recruiter']) ? (
            <RecruiterDashboard />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/recruiter/assessments/create"
        element={
          hasRoleAccess(['recruiter']) ? (
            <AssessmentCreateWizard />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/recruiter/assessments/:id"
        element={
          hasRoleAccess(['recruiter']) ? (
            <AssessmentDetails />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/recruiter/assessments/:id/edit"
        element={
          hasRoleAccess(['recruiter']) ? (
            <AssessmentCreateWizard />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/recruiter/jobs"
        element={
          hasRoleAccess(['recruiter']) ? (
            <RecruiterJobsManagement />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/recruiter/jobs/:jobId/applications"
        element={
          hasRoleAccess(['recruiter']) ? (
            <ApplicationsList />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/recruiter/approvals"
        element={
          hasRoleAccess(['recruiter', 'stakeholder']) ? (
            <ApprovalDashboard />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/recruiter/talent-pool"
        element={
          hasRoleAccess(['recruiter']) ? (
            <TalentPoolView />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/recruiter/jobs/:jobId/questionnaires/create"
        element={
          hasRoleAccess(['recruiter']) ? (
            <QuestionnaireBuilderWrapper />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/recruiter/jobs/:jobId/questionnaires/:questionnaireId/edit"
        element={
          hasRoleAccess(['recruiter']) ? (
            <QuestionnaireBuilderWrapper />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Assessment Lab - Start and run assessment */}
      <Route
        path="/assessment/:id"
        element={
          <ProtectedRoute><AssessmentLab /></ProtectedRoute>
        }
      />

      {/* Assessment Results */}
      <Route
        path="/assessment/:id/results"
        element={
          <ProtectedRoute><AssessmentResults /></ProtectedRoute>
        }
      />

      {/* Training Route - Accessible to all authenticated users */}
      <Route
        path="/training"
        element={
          <ProtectedRoute><TrainingDashboard /></ProtectedRoute>
        }
      />

      {/* Challenge Detail Route - For candidates to view challenge details */}
      <Route
        path="/challenge/:id"
        element={
          <ProtectedRoute><ChallengeDetail /></ProtectedRoute>
        }
      />

      {/* Candidate Dashboard */}
      <Route
        path="/candidate"
        element={
          isAuthenticated() && user?.role === 'candidate' ? (
            <CandidateDashboard user={user} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Public Job Listings */}
      <Route path="/jobs" element={<PublicJobListings />} />

      {/* Public Job Posting - supports both slug and ID */}
      <Route path="/jobs/:slugOrId" element={<JobPosting />} />

      {/* Public Job Application Form - no auth required */}
      <Route path="/jobs/:slugOrId/apply" element={<JobApplicationForm />} />

      {/* Magic Link Verification - no auth required */}
      <Route path="/auth/verify" element={<MagicLinkVerify />} />

      {/* Email Verification - no auth required */}
      <Route path="/verify-email" element={<EmailVerification />} />

      {/* Application Tracker - public, UUID-based access */}
      <Route path="/track/:uuid" element={<ApplicationTracker />} />

      {/* Post-interview candidate feedback - public, token-based access */}
      <Route path="/interview-feedback/:interviewId" element={<InterviewFeedback />} />

      {/* Questionnaire Response - public, token-based access */}
      <Route path="/questionnaire/:token" element={<QuestionnaireResponse />} />
      
      {/* Availability Picker - public, token-based access */}
      <Route path="/availability/:token" element={<AvailabilityPicker />} />

      {/* My Lab - any authenticated user with an assigned lab */}
      <Route path="/my-lab" element={<ProtectedRoute><MyLabPage /></ProtectedRoute>} />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <DrawerProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </DrawerProvider>
    </Router>
  );
}

export default App;
